import JsBarcode from 'jsbarcode';
import type { PackingInvoice, PackingInvoicePage } from '@/infrastructure/FulfillmentAdminClient';
import { kstLabelStamp } from '@/utils/fulfillmentTime';
import {
  LABEL_PAGE,
  blankAsDash,
  nameLinesOf,
  paginateByHeight,
  rowUnits,
  sheetCapacityOf,
  sheetDocument,
  sheetHtml,
  type SheetPage,
  type SheetRow,
} from './labelSheet';
import { NO_LOCATION, byWalkOrder } from './sheetRows';
import { pickerLocation, splitAcrossLocations } from './lineLocations';

/** The 100x150mm label stock, fed landscape; kept under its old name for the callers that size by it. */
export const PACKING_SHEET_PAGE = LABEL_PAGE;

/** One bar on a 203dpi thermal head is 0.125mm; three of them stay readable after the ribbon smears. */
const MODULE_MM = 0.375;

/** The strip between the title and 총수량 the barcode may take, so a long order number shrinks rather than collides. */
const BARCODE_MAX_WIDTH_MM = 50;

const BARCODE_HEIGHT_MM = 8;

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The order number as Code128, sized in millimetres so it reads the same on any printer. Drawn here
 * rather than by the printer because the sheet is plain html that has to carry its own bars.
 */
const barcodeOf = (orderId: number): string => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  // One unit per module with a ten-module quiet zone, so the viewBox width is the module count.
  JsBarcode(svg, String(orderId), { format: 'CODE128', width: 1, height: 40, margin: 10, displayValue: false });
  const modules = parseFloat(svg.getAttribute('width') ?? '');
  const height = parseFloat(svg.getAttribute('height') ?? '');
  if (!(modules > 0 && height > 0)) {
    throw new Error(`The barcode for order ${orderId} could not be drawn.`);
  }
  const widthMm = Math.min(modules * MODULE_MM, BARCODE_MAX_WIDTH_MM);
  svg.setAttribute('viewBox', `0 0 ${modules} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('width', `${widthMm}mm`);
  svg.setAttribute('height', `${BARCODE_HEIGHT_MM}mm`);
  svg.removeAttribute('style');
  return svg.outerHTML;
};

/**
 * The internal invoice ("PACKING") as a document of its own, laid out as the 10/7 label test fixed it: title
 * and order number top-left, the order barcode top-centre, 총수량 top-right, the six-column table, and the
 * footer on every sheet. The server decides the rows; this splits a row across its shelves, orders them for
 * the walk, wraps long names and cuts the sheets by the height the rows take. One renderer, whichever screen asks for the sheet [SSD-7207].
 */
export class PackingSheet {
  /** The rows in walk order, blanks as hyphens, cut into sheets by height. */
  public static pagesOf(invoice: PackingInvoice): Array<SheetPage<SheetRow>> {
    const rows: SheetRow[] = invoice.pages
      .flatMap((page: PackingInvoicePage) => page.rows)
      // A line packed from several shelves prints a row per shelf, each at its share [SSD-7236].
      .flatMap((row) =>
        splitAcrossLocations(row, (whole, location) => ({
          ...whole,
          locationCode: location.locationCode ?? NO_LOCATION,
          qty: location.qty,
        })),
      )
      .sort(byWalkOrder)
      .map((row, index) => ({
        no: index + 1,
        eanLast4: blankAsDash(row.eanLast4),
        name: row.productNameKr,
        size: blankAsDash(row.size),
        // WH#00 is no shelf to walk to, so its row prints with the location left blank [SSD-7436].
        location: blankAsDash(pickerLocation(row.locationCode)),
        qty: row.qty,
        isSampleSet: row.isSampleSet,
      }));
    return paginateByHeight(rows, (row) => rowUnits(nameLinesOf(row.name)), sheetCapacityOf(invoice.pages));
  }

  /** The sheets alone, one string each, for a print that carries them behind another sticker. */
  public static sheetsOf(invoice: PackingInvoice): string[] {
    if (invoice.pages.length === 0) {
      throw new Error(`The packing invoice for order ${invoice.orderId} came back with no pages.`);
    }
    const title = PackingSheet.titleOf(invoice);
    const barcodeSvg = barcodeOf(invoice.orderId);
    // The order's creation, never the print time: a reprint must stamp the same moment [§3.2].
    const stamp = kstLabelStamp(invoice.orderPlacedAt);
    const pages = PackingSheet.pagesOf(invoice);
    // The floor counts merchandise: the sample set prints a hyphen, so 총수량 and 합계 leave it out [SSD-7420].
    const totalQty = pages
      .flatMap((page) => page.rows)
      .filter((row) => row.isSampleSet !== true)
      .reduce((sum, row) => sum + row.qty, 0);
    return pages.map((page) => sheetHtml({ title, barcodeSvg, totalQty }, page, stamp));
  }

  public static htmlOf(invoice: PackingInvoice): string {
    return PackingSheet.documentOf(invoice, PackingSheet.sheetsOf(invoice));
  }

  /** The sheets already drawn for the invoice, as a document of their own. */
  public static documentOf(invoice: PackingInvoice, sheets: string[]): string {
    return sheetDocument(PackingSheet.titleOf(invoice), sheets);
  }

  private static titleOf(invoice: PackingInvoice): string {
    return `PACKING #${invoice.orderId}`;
  }
}
